import React, { Component } from 'react'
import { Card, Button, Table, Tag, Input, notification } from 'antd'
import { TableCol } from 'components/TableCol'
import { getTicketsInformation } from 'services/CeberusApiService'
import moment from 'moment'
import './DeskTicketInformation.scss'
import { TicketRequest } from 'models/request/TicketRequest'
import { TenantService } from 'connection/TenantService'
import { BaseRequest } from 'models/request/BaseRequest'

interface State {
  isLoading: boolean;
  ticketsInformation: any[];
  filter: {
    sequentialId: number | null;
    agentId: number | null;
    customerId: number | null;
  };
}

export class DeskTicketInformation extends Component<BaseRequest, State> {

  state = {
    isLoading: false,
    ticketsInformation: [],
    filter: {
      sequentialId: null,
      agentId: null,
      customerId: null
    }
  }

  ticketsMainColumns = [
    TableCol({
      title: 'Sequential Id',
      sort: (a, b) => a.sequentialId - b.sequentialId,
      filter: (value, record) =>
        record.sequentialId.toString().includes(value.toString())
    }),
    TableCol({
      title: 'Owner Identity',
      sort: (a, b) => a.ownerIdentity.localeCompare(b.ownerIdentity),
      filter: (value, record) =>
        record.ownerIdentity.toLowerCase().includes(value.toLowerCase())
    }),
    TableCol({
      title: 'Customer Identity',
      sort: (a, b) => a.customerIdentity.localeCompare(b.customerIdentity),
      filter: (value, record) =>
        record.customerIdentity.toLowerCase().includes(value.toLowerCase())
    }),
    TableCol({
      title: 'Agent Identity',
      sort: (a, b) => a.agentIdentity.localeCompare(b.agentIdentity),
      filter: (value, record) =>
        record.agentIdentity.toLowerCase().includes(value.toLowerCase())
    }),
    TableCol({
      title: 'Team',
      sort: (a, b) => a.team.localeCompare(b.team),
      filter: (value, record) =>
        record.team.toLowerCase().includes(value.toLowerCase()),
      render: team => (
        <span>
          <Tag color={'geekblue'} key={team}>
            {team}
          </Tag>
        </span>
      )
    }),
    TableCol({
      title: 'Status',
      sort: (a, b) => a.status.localeCompare(b.status),
      filter: (value, record) =>
        record.status.toLowerCase() === value.toLowerCase(),
      filters: [
        { text: 'Assigned	', value: 'Assigned' },
        { text: 'ClosedAttendant	', value: 'ClosedAttendant' },
        { text: 'ClosedClient', value: 'ClosedClient' },
        { text: 'Open', value: 'Open' },
        { text: 'Transferred', value: 'Transferred' },
        { text: 'Waiting', value: 'Waiting' }
      ]
    })
  ]

  ticketsDetailColumns = [
    TableCol({
      title: 'Id'
    }),
    TableCol({
      title: 'Parent Sequential Id'
    }),
    TableCol({
      title: 'Storage Date',
      render: storageDate => (
        <span>
          {storageDate ? moment(storageDate).format('DD/MM/YYYY HH:mm:ss') : ''}
        </span>
      )
    }),
    TableCol({
      title: 'Opening Date',
      key: 'openDate',
      render: openDate => (
        <span>
          {openDate ? moment(openDate).format('DD/MM/YYYY HH:mm:ss') : ''}
        </span>
      )
    }),
    TableCol({
      title: 'Closing Date',
      key: 'closeDate',
      render: closeDate => (
        <span>
          {closeDate ? moment(closeDate).format('DD/MM/YYYY HH:mm:ss') : ''}
        </span>
      )
    }),
    TableCol({
      title: 'Closed By',
      key: 'closedBy'
    })
  ]

  getTicketFilter() {
    let filterStr = ''
    const suffixFilter = ' and '

    if (Object.keys(this.state.filter).length > 0) {
      filterStr += '('

      if (this.state.filter.sequentialId) {
        const sequentialIdInt = !isNaN(Number(this.state.filter.sequentialId))
          ? this.state.filter.sequentialId
          : 0
        filterStr += `SequentialId eq ${sequentialIdInt}${suffixFilter}`
      }
      if (this.state.filter.agentId) {
        filterStr += `AgentIdentity eq '${this.state.filter.agentId}'${suffixFilter}`
      }
      if (this.state.filter.customerId) {
        filterStr += `substringof('${this.state.filter.customerId}',CustomerIdentity)${suffixFilter}`
      }
      filterStr = filterStr.substring(0, filterStr.lastIndexOf(suffixFilter))

      filterStr += ')'
    }
    return filterStr
  }

  hasSelectedFilter() {
    return (
      this.state.filter.customerId ||
      this.state.filter.sequentialId ||
      this.state.filter.agentId
    )
  }

  async getTicketsByFilter() {
    this.setState({ isLoading: true })

    try {
      this.setState({ ticketsInformation: [] })
      if (this.hasSelectedFilter()) {
        const ticketRequest = new TicketRequest()
        ticketRequest.ClusterName = TenantService.getSelectedTenant().name
        ticketRequest.BotIdentifier = this.props.BotIdentifier
        ticketRequest.Filter = this.getTicketFilter()

        let { items } = await getTicketsInformation(ticketRequest)

        items = items.map(ticket => {
          return {
            key: ticket.id,
            id: ticket.id,
            sequentialId: ticket.sequentialId || '',
            parentSequentialId: ticket.parentSequentialId || '',
            ownerIdentity: ticket.ownerIdentity || '',
            customerIdentity: ticket.customerIdentity || '',
            agentIdentity: ticket.agentIdentity,
            storageDate: ticket.storageDate || '',
            openDate: ticket.openDate || '',
            closeDate: ticket.closeDate || '',
            team: ticket.team || '',
            status: ticket.status || '',
            closedBy: ticket.closedBy || ''
          }
        })

        this.setState({ ticketsInformation: items })
      } else {
        const argsNotification = {
          message: 'Ticket Information',
          description:
            'Enter at least one filter before clicking the search button.',
          duration: 5
        }
        notification.error(argsNotification)
      }
    } catch (e) {
      console.error(e)
    } finally {
      this.setState({ isLoading: false })
    }
  }

  handleInputChange = (e) => {
    const value = e.target.value.trim()
    const name = e.target.name

    this.setState((prevState) => ({
      filter: { 
        ...prevState.filter, 
        [name]: value }
    }))
  }

  render() {
    return (
      <div>
        <Card>
          <div className="flex justify-between items-center">
            <div className="w-33">
              <span>Sequential Id</span>
              <Input
                name="sequentialId"
                onChange={this.handleInputChange}
                onPressEnter={() => this.getTicketsByFilter()}
              />
            </div>
            <div className="w-33">
              <span>Agent Identity</span>
              <Input
                name="agentId"
                onChange={this.handleInputChange}
                onPressEnter={() => this.getTicketsByFilter()}
              />
            </div>
            <div className="w-33">
              <span>Customer Identity</span>
              <Input
                name="customerId"
                onChange={this.handleInputChange}
                onPressEnter={() => this.getTicketsByFilter()}
              />
            </div>
          </div>
          <div className="flex justify-end mt2">
            <Button
              type="primary"
              icon="search"
              onClick={() => this.getTicketsByFilter()}
            >
              Search
            </Button>
          </div>
        </Card>
        {this.hasSelectedFilter() && (
          <Card className="mt2">
            <Table
              className="table-thead-color"
              title={() => 'Tickets'}
              locale={{
                emptyText:
                  'No tickets found. please, try filtering other informations'
              }}
              dataSource={this.state.ticketsInformation}
              columns={this.ticketsMainColumns}
              loading={this.state.isLoading}
              expandedRowRender={ticketRow => {
                const ticketDetails = []
                ticketDetails.push({
                  key: ticketRow.id,
                  id: ticketRow.id,
                  parentSequentialId: ticketRow.parentSequentialId,
                  storageDate: ticketRow.storageDate,
                  openDate: ticketRow.openDate,
                  closeDate: ticketRow.closeDate,
                  closedBy: ticketRow.closedBy
                })
                return (
                  <Table
                    dataSource={ticketDetails}
                    columns={this.ticketsDetailColumns}
                    pagination={false}
                    loading={this.state.isLoading}
                  />
                )
              }}
            />
          </Card>
        )}
      </div>
    )
  }
}